import React, { FC, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Organization } from 'models/organization';
import { useLazyQuery, useMutation } from '@apollo/client';
import { organizationLookupTemplate } from 'apollo/templates/organization';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import OrganizationLookup, {
    OrganizationLookupFormData,
} from 'components/shared/organization-lookup/organization-lookup.component';
import { getEnabledCountries } from 'utils/country-helper';
import { Country } from 'models/country';
import CustomerList from 'components/client-directory/add-white-label-clinic-dialog/customer-list/customer-list.component';
import { connectSupplierToLabTemplate } from 'apollo/templates/client-directory';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type AddSupplierDialogProps = {
    dialogOpen: boolean;
    onCloseDialog: () => void;
    onSupplierAdded: () => void;
};
const AddSupplierDialog: FC<AddSupplierDialogProps> = ({
    dialogOpen,
    onCloseDialog,
    onSupplierAdded,
}: AddSupplierDialogProps) => {
    const { t: translate } = useTranslation(['organization']);
    const { showBoundary } = useDenthubErrorBoundary();
    const [activeStep, setActiveStep] = useState('lookup');
    const [errorMessage, setErrorMessage] = useState<string>();
    const [isLoading, setIsLoading] = useState(false);
    const [suppliers, setSuppliers] = useState<(Partial<Organization> & { isAlreadyConnected: boolean })[]>([]);
    const [_, setCountryCode] = useState<string>();
    const EnabledCountries = getEnabledCountries();
    const sweden = EnabledCountries.find((country) => country.alpha2Code === 'SE');
    const [country] = useState<Country | undefined>(sweden);

    const [connectSupplierToLab] = useMutation(connectSupplierToLabTemplate, {
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            onCloseDialog();
            onSupplierAdded();
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const [organizationLookup] = useLazyQuery(organizationLookupTemplate, {
        fetchPolicy: 'network-only',
        onError: (error) => {
            const [firstError] = error.graphQLErrors;
            setIsLoading(false);
            if (firstError.message.includes('HitHorizons: No results found')) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_NOT_FOUND'));
                return;
            }
            if (firstError.message.includes('organization with same hitHorizonsId already exists')) {
                setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_ALREADY_EXISTS'));
                return;
            }
            showBoundary(firstError);
        },
        onCompleted: ({ organizationLookup }) => {
            const organizations = organizationLookup?.map((org: any) => ({
                _id: org.organizationDbId,
                hitHorizonsId: org.hitHorizonsId,
                name: org.name,
                type: org.type,
                isAlreadyConnected: org.isAlreadyConnected,
                officialName: org.name,
                organizationNumber: org.organizationNumber,
                contact: {
                    communication: { site: org.site || undefined },
                    address: org.address,
                },
                invoicing: {
                    vatNo: org.vatNumber,
                    costCenter: org.costCenter,
                },
            }));
            setIsLoading(false);
            // A lab is never its own supplier; the backend refuses it too.
            const ownOrganizationId = currentOrgVar()?._id;
            const candidates = organizations.filter(
                (organization: { _id?: string }) => organization._id !== ownOrganizationId,
            );
            if (organizations.length && !candidates.length) {
                setErrorMessage(translate('organization:VALIDATION.CANNOT_ADD_OWN_LAB_AS_SUPPLIER'));
                return;
            }
            if (candidates.length) {
                setSuppliers(candidates);
                setActiveStep('connect_supplier');
                return;
            }
            setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_NOT_FOUND'));
        },
    });

    const handleOrganizationSelected = (selectOrganizationForm: OrganizationLookupFormData) => {
        setIsLoading(true);
        setCountryCode(selectOrganizationForm.countryCode);
        organizationLookup({
            variables: {
                organizationNumber: selectOrganizationForm.organizationNumber,
                countryCode: selectOrganizationForm.countryCode,
                checkWhitelabel: false,
                type: 'lab',
                // A supplier must already exist as a lab in Denthub, so never fall
                // back to HitHorizons — those records have no organization id.
                existingOnly: true,
            },
        });
    };

    const handleCloseDialog = () => {
        onCloseDialog();
        resetForm();
    };

    const resetForm = () => {
        setActiveStep('lookup');
        setErrorMessage(undefined);
        setIsLoading(false);
        setSuppliers([]);
        setCountryCode(undefined);
    };

    const handleSupplierAdded = (supplierId: string) => {
        if (!supplierId) {
            setErrorMessage(translate('organization:VALIDATION.ORGANIZATION_NOT_FOUND'));
            return;
        }
        connectSupplierToLab({
            variables: {
                supplierId: supplierId,
            },
        });
    };

    const getStepContent = (step: string) => {
        switch (step) {
            case 'lookup':
                return (
                    <OrganizationLookup
                        onOrganizationSelected={handleOrganizationSelected}
                        onClose={handleCloseDialog}
                        country={country}
                        onOrganizationChanged={() => setErrorMessage(undefined)}
                    />
                );
            case 'connect_supplier':
                return (
                    <CustomerList
                        customers={suppliers}
                        onSelectCustomer={handleSupplierAdded}
                        addButtonTitle={translate('client-directory:ADD_SUPPLIER')}
                    />
                );
            default:
                break;
        }
    };

    return (
        <Dialog fullWidth maxWidth="md" open={dialogOpen} onClose={handleCloseDialog}>
            <DialogTitle>{translate('client-directory:ADD_SUPPLIER')}</DialogTitle>
            <DialogContent>
                {errorMessage && (
                    <Box mb={2}>
                        <Alert severity="error">{errorMessage}</Alert>
                    </Box>
                )}
                {isLoading ? (
                    <Box sx={{ minHeight: '200px' }} display="flex" alignItems="center" justifyContent="center">
                        <CircularProgress size={80} />
                    </Box>
                ) : (
                    getStepContent(activeStep)
                )}
            </DialogContent>
            {activeStep === 'connect_supplier' ? (
                <DialogActions sx={{ justifyContent: 'flex-start' }}>
                    <Button onClick={handleCloseDialog}>{translate('common:CANCEL')}</Button>
                </DialogActions>
            ) : undefined}
        </Dialog>
    );
};

export default AddSupplierDialog;
